import { api, getHeaders } from '@/api';
import type { QueryCriteria, ResponseType } from '@/api/types';
import { buildQueryParams, validateResponse } from '@/api/utils';
import { queryOptions } from '@tanstack/react-query';

import {
  drillExApiResponseSchema,
  drillExArrayApiResponseSchema,
  drillTypeArrayApiResponseSchema,
  type ApiResponse,
  type DrillEx,
  type DrillType
} from './schema';

const drillsEndpoints = {
  getDrillById: async (id: string): Promise<DrillEx> => {
    const response = await api.get<ApiResponse<DrillEx>>(`/ism/api/drills/${id}?res.Type=hierarchy`, getHeaders());
    const parsed = validateResponse(drillExApiResponseSchema, response.data) as ApiResponse<DrillEx>;
    return parsed.Data;
  },

  saveDrill: async (drill: DrillEx): Promise<{ Id: string }> => {
    if (drill.Id) {
      const response = await api.put<{ Id: string }>(`/ism/api/drills/ex`, drill, getHeaders());
      return response.data;
    }
    const response = await api.post<{ Id: string }>('/ism/api/drills/ex', drill, getHeaders());
    return response.data;
  },

  deleteDrill: async (id: string): Promise<void> => {
    await api.delete(`/ism/api/drills/${id}`, getHeaders());
  },

  getDrillTypes: async (criteria?: QueryCriteria): Promise<DrillType[]> => {
    const query = buildQueryParams(criteria);
    const response = await api.get<ApiResponse<DrillType[]>>(`/ism/api/drillTypes?${query}`, getHeaders());
    const parsed = validateResponse(drillTypeArrayApiResponseSchema, response.data) as ApiResponse<DrillType[]>;
    return parsed.Data;
  },

  getNextDrills: async (criteria?: QueryCriteria): Promise<DrillEx[]> => {
    const query = buildQueryParams(criteria);
    const response = await api.get<ApiResponse<DrillEx[]>>(`/ism/api/drills/report/next-drills?${query}`, getHeaders());
    const parsed = validateResponse(drillExArrayApiResponseSchema, response.data) as ApiResponse<DrillEx[]>;
    return parsed.Data;
  },

  getDrills: async (criteria?: QueryCriteria, resType: ResponseType = 'flat'): Promise<DrillEx[]> => {
    const query = buildQueryParams(criteria, resType);
    const response = await api.get<ApiResponse<DrillEx[]>>(`/ism/api/drills?${query}`, getHeaders());
    const parsed = validateResponse(drillExArrayApiResponseSchema, response.data) as ApiResponse<DrillEx[]>;
    return parsed.Data;
  }
};

export const drillsQueries = {
  all: () => ['drills'],

  lists: () => [...drillsQueries.all(), 'list'],
  list: (criteria?: QueryCriteria, resType: ResponseType = 'flat') =>
    queryOptions({
      queryKey: [...drillsQueries.lists(), criteria, resType],
      queryFn: () => drillsEndpoints.getDrills(criteria, resType)
    }),

  details: () => [...drillsQueries.all(), 'detail'],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...drillsQueries.details(), id],
      queryFn: () => drillsEndpoints.getDrillById(id),
      staleTime: 5000
    }),

  nextDrills: (criteria?: QueryCriteria) =>
    queryOptions({
      queryKey: [...drillsQueries.all(), 'nextDrills', criteria],
      queryFn: () => drillsEndpoints.getNextDrills(criteria)
    })
};

export const drillTypesQueries = {
  all: () => ['drillTypes'],

  lists: () => [...drillTypesQueries.all(), 'list'],
  list: (criteria?: QueryCriteria) =>
    queryOptions({
      queryKey: [...drillTypesQueries.lists(), criteria],
      queryFn: () => drillsEndpoints.getDrillTypes(criteria)
    })
};

export { drillsEndpoints };
